import type {
  StandardizedVoyage,
  StandardizedVoyagePaginatedResponse,
  StandardizedVoyageUpdateRequest,
  EUETSLegPart,
} from '@/types';
import { apiClient } from './apiClient';

export class VoyageService {
  /**
   * Get all standardized voyages with pagination
   */
  static async getAllStandardizedVoyagesPaginated(
    userId?: string | null,
    pageSize: number = 25,
    nextPageToken?: string,
    sortBy: string = 'atd',
    sortDirection: 'asc' | 'desc' = 'desc'
  ): Promise<StandardizedVoyagePaginatedResponse> {
    const params = new URLSearchParams();
    params.append('page_size', pageSize.toString());
    params.append('sort_by', sortBy);
    params.append('sort_direction', sortDirection);

    if (nextPageToken) {
      params.append('next_page_token', nextPageToken);
    }

    if (userId) {
      params.append('userId', userId);
    }

    return apiClient.get<StandardizedVoyagePaginatedResponse>(
      `/voyages/paginated?${params.toString()}`
    );
  }

  /**
   * Get a specific standardized voyage by ID
   */
  static async getStandardizedVoyageById(
    voyageId: string,
    vesselId: string
  ): Promise<StandardizedVoyage> {
    return apiClient.get<StandardizedVoyage>(
      `/voyages/${voyageId}?vesselId=${encodeURIComponent(vesselId)}`
    );
  }

  /**
   * Update a standardized voyage
   */
  static async updateStandardizedVoyage(
    voyageId: string,
    vesselId: string,
    updates: StandardizedVoyageUpdateRequest
  ): Promise<void> {
    return apiClient.put<void>(
      `/voyages/${voyageId}?vesselId=${encodeURIComponent(vesselId)}`,
      updates
    );
  }

  /**
   * Delete a standardized voyage
   */
  static async deleteStandardizedVoyage(voyageId: string, vesselId: string): Promise<void> {
    await apiClient.delete<void>(`/voyages/${voyageId}/vessel/${vesselId}`);
  }

  /**
   * Fetch EU ETS leg parts for a specific voyage
   */
  static async fetchVoyageLegs(vesselId: string, voyageId: string): Promise<EUETSLegPart[]> {
    return apiClient.get<EUETSLegPart[]>(`/voyages/vessel/${vesselId}/voyage/${voyageId}/legs`);
  }
}
